'use client';

import React, { useEffect, useRef } from 'react';
import { animate, useInView, motion, useReducedMotion } from 'framer-motion';
import { Factory, TrendingUp, Truck, PackageCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Animated Counter Component
function AnimatedCounter({ from, to, formatting }: { from: number; to: number; formatting: any }) {
  const nodeRef = useRef<HTMLSpanElement>(null);
  const isInView = useInView(nodeRef, { once: true });
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    if (isInView && !shouldReduceMotion && nodeRef.current) {
      const node = nodeRef.current;
      const controls = animate(from, to, {
        duration: 2,
        ease: 'easeOut',
        onUpdate(value) {
          node.textContent = value.toLocaleString(formatting?.locales, formatting);
        },
      });
      return () => controls.stop();
    }
  }, [isInView, shouldReduceMotion, from, to, formatting]);

  return (
    <>
      <span className="sr-only">{to.toLocaleString(formatting?.locales, formatting)}</span>
      <span ref={nodeRef} aria-hidden="true">
        {shouldReduceMotion || !isInView ? to.toLocaleString(formatting?.locales, formatting) : from}
      </span>
    </>
  );
}

interface Stat {
  iconName: string;
  from: number;
  to: number;
  label: string;
  unit: string;
  formatting?: any;
}

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  Factory,
  TrendingUp,
  Truck,
  PackageCheck,
};

export const CompanyStatsClient = ({ stats, children }: { stats: Stat[]; children: React.ReactNode }) => {
  return (
    <motion.section
      id="company-stats"
      className="py-16 md:py-24 bg-background"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
    >
      <div className="container mx-auto px-4">
        <motion.div
          className="flex flex-col items-center text-center mb-12"
          variants={{ hidden: { opacity: 0, y: -50 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } }}
        >
          {children}
        </motion.div>

        <div className="mt-10">
          <dl className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => {
              const IconComponent = iconMap[stat.iconName];
              return (
                <motion.div
                  key={stat.label}
                  className="relative overflow-hidden rounded-lg bg-muted/50 p-6"
                  variants={{
                    hidden: { opacity: 0, y: 50 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
                  }}
                >
                  <div className="relative z-10 flex h-full flex-col justify-between">
                    <div>
                      <dd className="text-4xl font-bold text-primary">
                        <AnimatedCounter from={stat.from} to={stat.to} formatting={stat.formatting} />
                        <span className="text-xl font-medium text-foreground/80">{stat.unit}</span>
                      </dd>
                      <dt className="mt-1 text-lg font-medium text-foreground">{stat.label}</dt>
                    </div>
                  </div>
                  {IconComponent && (
                    <IconComponent
                      className="absolute right-0 top-1/2 -translate-y-1/2 h-28 w-28 text-primary opacity-10"
                      aria-hidden="true"
                    />
                  )}
                </motion.div>
              );
            })}
          </dl>
        </div>
      </div>
    </motion.section>
  );
};
